import { Add, Edit, Close } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomButton from 'lib/designSystem/Button';
import { getBestTextColorFromHex, showAlert, validatePermissions } from 'lib/functions/utils';
import {
  addBusinessTag,
  getAllCategories,
  getBusinessTags,
  removeBusinessTag,
} from 'lib/api/tags';

interface TagItem {
  id: number;
  name: string;
  description?: string;
  color: string; // HEX
}

interface CategoryItem {
  id: number;
  name: string;
  tags: TagItem[];
}

interface TagsProps {
  businessId: string; // businessIdentifier
}

const Tags: React.FC<TagsProps> = (props) => {
  const { businessId } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.user);

  const [loading, setLoading] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [editedTags, setEditedTags] = useState<TagItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [selectedTagId, setSelectedTagId] = useState<number | ''>('');

  const canEdit = validatePermissions(user, 'tagging');

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId],
  );

  const existingTagIds = useMemo(
    () => new Set((editing ? editedTags : tags).map((t) => t.id)),
    [editing, editedTags, tags],
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tagsRes, catsRes] = await Promise.all([
        getBusinessTags(dispatch, businessId),
        getAllCategories(dispatch),
      ]);

      const normalizeTags = (res: any): TagItem[] => {
        if (!res) return [];
        if (Array.isArray(res)) return res as TagItem[];
        if (Array.isArray(res?.tags)) return res.tags as TagItem[];
        if (Array.isArray(res?.data)) return res.data as TagItem[];
        return [];
      };

      setTags(normalizeTags(tagsRes));
      setEditedTags(normalizeTags(tagsRes));
      setCategories(Array.isArray(catsRes?.categories) ? catsRes.categories : Array.isArray(catsRes) ? catsRes : []);
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error al cargar los tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!businessId) return;
    void fetchData();
  }, [businessId]);

  const handleEnterEdit = () => {
    setEditedTags(tags);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditedTags(tags);
    setEditing(false);
  };

  const handleRemoveEditedTag = (tagId: number) => {
    setEditedTags((prev) => prev.filter((t) => t.id !== tagId));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const originalIds = new Set(tags.map((t) => t.id));
      const newIds = new Set(editedTags.map((t) => t.id));

      const toAdd = editedTags.filter((t) => !originalIds.has(t.id));
      const toRemove = tags.filter((t) => !newIds.has(t.id));

      await Promise.all([
        ...toAdd.map((t) =>
          addBusinessTag(dispatch, {
            businessIdentifier: businessId,
            tagId: t.id,
          }),
        ),
        ...toRemove.map((t) =>
          removeBusinessTag(dispatch, {
            businessIdentifier: businessId,
            tagId: t.id,
          }),
        ),
      ]);

      showAlert(dispatch, 'success', 'Tags actualizados correctamente');
      await fetchData();
      setEditing(false);
    } catch (error) {
      showAlert(dispatch, 'error', 'No se pudieron guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    if (!editing) setEditing(true);
    setSelectedCategoryId('');
    setSelectedTagId('');
    setOpenAddDialog(true);
  };

  const handleConfirmAdd = () => {
    if (!selectedTagId) return;
    const tag = categories
      .flatMap((c) => c.tags)
      .find((t) => t.id === selectedTagId);
    if (!tag) return;
    if (!existingTagIds.has(tag.id)) {
      setEditedTags((prev) => [...prev, tag]);
    }
    setOpenAddDialog(false);
  };

  if (loading) return <CenteredCircularProgress relative />;

  const listToRender = editing ? editedTags : tags;
  const hasTags = listToRender.length > 0;

  return (
    <Paper
      sx={{
        p: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        position: 'relative',
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.2rem',
              lineHeight: 1.1,
            }}
          >
            Tags
          </Typography>
          <Chip
            label={editing ? 'Modo Edición' : 'Resumen'}
            size="small"
            color={editing ? 'warning' : 'primary'}
            variant="outlined"
            sx={{ height: '22px', fontSize: '0.75rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!editing && canEdit && (
            <>
              <IconButton
                size="small"
                onClick={handleEnterEdit}
                sx={{
                  padding: 1,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  },
                }}
              >
                <Edit sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleOpenAdd}
                sx={{
                  padding: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  },
                }}
              >
                <Add sx={{ fontSize: '1.1rem', color: 'success.main' }} />
              </IconButton>
            </>
          )}
          {editing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CustomButton
                variant="outlined"
                size="small"
                color="secondary"
                onClick={handleCancel}
              >
                Cancelar
              </CustomButton>
              <CustomButton
                variant="contained"
                size="small"
                color="primary"
                onClick={handleSave}
                disabled={!canEdit}
              >
                Guardar
              </CustomButton>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {hasTags ? (
          listToRender.map((tag) => (
            <Tooltip key={tag.id} title={tag.description || ''} arrow>
              <Chip
                label={tag.name}
                sx={{
                  backgroundColor: tag.color,
                  color: getBestTextColorFromHex(tag.color),
                  fontWeight: 600,
                }}
                {...(editing
                  ? {
                    onDelete: () => handleRemoveEditedTag(tag.id),
                    deleteIcon: (
                      <Close sx={{ color: getBestTextColorFromHex(tag.color) }} />
                    ),
                  }
                  : {})}
              />
            </Tooltip>
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.85rem', color: 'text.disabled', fontStyle: 'italic' }}
          >
            No hay tags asociados
          </Typography>
        )}
      </Box>

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Agregar Tag
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Categoría</InputLabel>
              <Select
                labelId="category-select-label"
                label="Categoría"
                value={selectedCategoryId}
                onChange={(e) => {
                  setSelectedCategoryId(e.target.value as number);
                  setSelectedTagId('');
                }}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedCategory}>
              <InputLabel id="tag-select-label">Tag</InputLabel>
              <Select
                labelId="tag-select-label"
                label="Tag"
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value as number)}
              >
                {(selectedCategory?.tags || [])
                  .filter((t) => !existingTagIds.has(t.id))
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={t.name}
                          sx={{
                            backgroundColor: t.color,
                            color: getBestTextColorFromHex(t.color),
                            height: 22,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                          {t.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setOpenAddDialog(false)} color="secondary">
            Cancelar
          </CustomButton>
          <CustomButton onClick={handleConfirmAdd} color="primary" disabled={!selectedTagId}>
            Agregar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Tags;
